/**
 * DiffDetailsPanel — the shared right rail for the diff/review surface.
 *
 * Unifies the two drifted copies (viewer + studio) into one component both
 * mount, and raises the UX. Driven by the selected `EntityRef` plus the diff
 * `changeSet` (and the before/after module trees for live values):
 *
 *   - module / interface → the module's before → after detail: fields, labels
 *     and interfaces, each row showing the actual transition (old struck, new
 *     highlighted), colour-coded, unchanged dimmed. A whole add/remove lists
 *     content plainly (no redundant per-row markers).
 *   - process / subprocess → what changed in the flow: added / removed steps
 *     (rendered with readable `caller → callee.interface` names), reorder and
 *     owner-move notes, rename.
 *   - view → minimal status detail.
 *
 * The module shapes are intentionally MINIMAL structural types (`DetailsModule`
 * …) so both the viewer's scene `Module` and studio's wire `Module` satisfy
 * them without a shared model dependency.
 */

import { useMemo } from "react";
import type {
  ChangeSet,
  EntityRef,
  ModuleDiff,
  ProcessDiff,
  SubprocessDiff,
  Status,
  StepKey,
} from "@archlang/engine";
import { interfaceDisplayName } from "../../displayNames.js";
import { displayLabel, displayPath } from "../../displayLabel.js";

// ── minimal structural inputs (both consumers' Module types satisfy these) ────

export interface DetailsEntry {
  readonly path: readonly string[];
  readonly value: unknown;
}
export interface DetailsInterface {
  readonly name: string;
  readonly typeName: string;
  readonly anonymous?: boolean;
}
export interface DetailsModule {
  readonly id?: string;
  readonly name: string;
  readonly typeName: string;
  readonly fields: readonly DetailsEntry[];
  readonly aspects: readonly DetailsEntry[];
  readonly interfaces: readonly DetailsInterface[];
  readonly children: readonly DetailsModule[];
}

export interface DiffDetailsPanelProps {
  readonly selected: EntityRef | null;
  readonly before: readonly DetailsModule[];
  readonly after: readonly DetailsModule[];
  readonly changeSet: ChangeSet;
}

// `plain` = a row inside a whole add/remove: the child trivially shares the
// parent's fate, so we list it as a neutral key/value with no per-row marker
// (the header status pill already says added/removed).
type RowStatus = "added" | "removed" | "modified" | "unchanged" | "plain";

const SIGIL: Record<RowStatus, string> = {
  added: "+",
  modified: "~",
  removed: "−",
  unchanged: "·",
  plain: "",
};

// ── shared helpers ────────────────────────────────────────────────────────────

function fmtValue(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "object" && v !== null && "text" in v) return String((v as { text: unknown }).text);
  if (typeof v === "object" && v !== null && "value" in v) return String((v as { value: unknown }).value);
  return String(v);
}

function StatBadge({ status }: { status: RowStatus }) {
  return (
    <span className={"dd-marker dd-marker--" + status} aria-label={status} title={status}>
      {SIGIL[status]}
    </span>
  );
}

function changedCount(rows: { status: RowStatus }[]): number {
  return rows.filter((r) => r.status !== "unchanged" && r.status !== "plain").length;
}

// ── component (dispatch) ──────────────────────────────────────────────────────

export function DiffDetailsPanel({ selected, before, after, changeSet }: DiffDetailsPanelProps) {
  // id → display name, for rendering process step keys readably.
  const idToName = useMemo(() => {
    const m = new Map<string, string>();
    const walk = (mods: readonly DetailsModule[]) => {
      for (const x of mods) {
        if (x.id) m.set(x.id, x.name);
        walk(x.children);
      }
    };
    walk(before);
    walk(after);
    return m;
  }, [before, after]);

  if (!selected) return <EmptyPanel />;

  if (selected.kind === "module" || selected.kind === "interface") {
    const id = selected.kind === "module" ? selected.id : selected.moduleId;
    return <ModuleDetails id={id} before={before} after={after} changeSet={changeSet} />;
  }
  if (selected.kind === "process") {
    const diff = changeSet.processes.get(selected.id);
    return diff ? <ProcessDetails diff={diff} kind="process" idToName={idToName} /> : <EmptyPanel />;
  }
  if (selected.kind === "subprocess") {
    const diff = changeSet.subprocesses.get(selected.id);
    return diff ? <ProcessDetails diff={diff} kind="subprocess" idToName={idToName} /> : <EmptyPanel />;
  }
  // view
  const vdiff = changeSet.views.get(selected.id);
  return vdiff ? <ViewDetails id={vdiff.id} status={vdiff.status} /> : <EmptyPanel />;
}

function EmptyPanel() {
  return (
    <aside className="dd-panel dd-panel--empty">
      <div className="dd-empty">
        <div className="dd-empty-icon" aria-hidden>
          ⤳
        </div>
        <div className="dd-empty-title">Nothing selected</div>
        <div className="dd-empty-hint">Pick a changed entity to inspect what changed.</div>
      </div>
    </aside>
  );
}

function StatusPill({ status }: { status: Status }) {
  if (status === "unchanged") return null;
  return <span className={"dd-status dd-status--" + status}>{status}</span>;
}

// ── module details ────────────────────────────────────────────────────────────

function findById(modules: readonly DetailsModule[], id: string): DetailsModule | null {
  for (const m of modules) {
    if (m.id === id) return m;
    const nested = m.children ? findById(m.children, id) : null;
    if (nested) return nested;
  }
  return null;
}

function findModuleDiff(diffs: ChangeSet["modules"], id: string): ModuleDiff | null {
  for (const [mid, d] of diffs) {
    if (mid === id) return d;
    const nested = findModuleDiff(d.children, id);
    if (nested) return nested;
  }
  return null;
}

interface EntryRow {
  readonly key: string;
  readonly status: RowStatus;
  readonly before: string;
  readonly after: string;
}

function diffEntries(
  before: readonly DetailsEntry[] | undefined,
  after: readonly DetailsEntry[] | undefined,
  containerStatus: Status | undefined,
): EntryRow[] {
  const byBefore = new Map((before ?? []).map((r) => [r.path.join("."), r.value]));
  const byAfter = new Map((after ?? []).map((r) => [r.path.join("."), r.value]));
  const keys = [...new Set([...byBefore.keys(), ...byAfter.keys()])].sort();
  const whole = containerStatus === "added" ? "after" : containerStatus === "removed" ? "before" : null;
  return keys.map((key) => {
    const bv = fmtValue(byBefore.get(key));
    const av = fmtValue(byAfter.get(key));
    if (whole) {
      const v = whole === "after" ? av : bv;
      return { key, status: "plain", before: v, after: v };
    }
    let status: RowStatus;
    if (!byBefore.has(key)) status = "added";
    else if (!byAfter.has(key)) status = "removed";
    else status = bv !== av ? "modified" : "unchanged";
    return { key, status, before: bv, after: av };
  });
}

interface IfaceRow {
  readonly key: string;
  readonly name: string;
  readonly status: RowStatus;
  readonly typeChange?: { from: string; to: string };
}

function interfaceRows(diff: ModuleDiff | null, display: DetailsModule): IfaceRow[] {
  const whole = diff?.status === "added" || diff?.status === "removed";
  if (diff && !whole) {
    return [...diff.interfaces.values()].map((i) => ({
      key: i.name,
      name: interfaceDisplayName(i),
      status: (i.status === "renamed" ? "modified" : i.status) as RowStatus,
      typeChange: i.typeChange ? { from: i.typeChange.from, to: i.typeChange.to } : undefined,
    }));
  }
  return display.interfaces.map((i) => ({
    key: i.name,
    name: `${i.typeName} ${interfaceDisplayName(i)}`,
    status: "plain",
  }));
}

function ModuleDetails({
  id,
  before,
  after,
  changeSet,
}: {
  id: string;
  before: readonly DetailsModule[];
  after: readonly DetailsModule[];
  changeSet: ChangeSet;
}) {
  const data = useMemo(() => {
    const b = findById(before, id);
    const a = findById(after, id);
    const display = a ?? b;
    if (!display) return null;
    const diff = findModuleDiff(changeSet.modules, id);
    const fieldRows = diffEntries(b?.fields, a?.fields, diff?.status);
    const labelRows = diffEntries(b?.aspects, a?.aspects, diff?.status);
    const ifaceRows = interfaceRows(diff, display);
    return { display, diff, fieldRows, labelRows, ifaceRows };
  }, [id, before, after, changeSet]);

  if (!data) return <EmptyPanel />;
  const { display, diff, fieldRows, labelRows, ifaceRows } = data;
  const status = (diff?.status ?? "unchanged") as Status;
  const isWhole = status === "added" || status === "removed";
  const t = { added: 0, removed: 0, modified: 0 };
  for (const r of [...fieldRows, ...labelRows, ...ifaceRows]) {
    if (r.status === "added" || r.status === "removed" || r.status === "modified") t[r.status]++;
  }

  return (
    <aside className="dd-panel">
      <header className="dd-header">
        <div className="dd-eyebrow">
          <span className="dd-kind">{display.typeName}</span>
          <StatusPill status={status} />
        </div>
        <h2 className="dd-name">{displayPath(display.name)}</h2>

        {diff?.nameChange && <RenameLine from={diff.nameChange.from} to={diff.nameChange.to} />}

        {isWhole ? (
          <div className="dd-summary dd-summary--quiet">
            {status === "added" ? "New module" : "Removed module"}
          </div>
        ) : (
          <ChangeChips added={t.added} modified={t.modified} removed={t.removed} emptyLabel="No attribute changes" />
        )}
      </header>

      <div className="dd-body">
        <EntrySection title="Fields" rows={fieldRows} />
        <EntrySection title="Labels" rows={labelRows} />
        <InterfaceSection rows={ifaceRows} />
      </div>
    </aside>
  );
}

function EntrySection({ title, rows }: { title: string; rows: EntryRow[] }) {
  if (rows.length === 0) return null;
  const changed = changedCount(rows);
  return (
    <section className="dd-section">
      <SectionHead title={title} count={changed} />
      <ul className="dd-list">
        {rows.map((r) => (
          <li key={r.key} className={"dd-row dd-row--" + r.status}>
            {r.status !== "plain" && <StatBadge status={r.status} />}
            <span className="dd-key">{r.key}</span>
            <span className="dd-val">
              {r.status === "modified" ? (
                <>
                  <span className="dd-old">{r.before}</span>
                  <span className="dd-val-arrow">→</span>
                  <span className="dd-new">{r.after}</span>
                </>
              ) : r.status === "removed" ? (
                <span className="dd-old">{r.before}</span>
              ) : r.status === "added" ? (
                <span className="dd-new">{r.after}</span>
              ) : r.status === "plain" ? (
                r.after
              ) : (
                <span className="dd-same">{r.after}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function InterfaceSection({ rows }: { rows: IfaceRow[] }) {
  if (rows.length === 0) return null;
  const changed = changedCount(rows);
  return (
    <section className="dd-section">
      <SectionHead title="Interfaces" count={changed} />
      <ul className="dd-list">
        {rows.map((r) => (
          <li key={r.key} className={"dd-row dd-row--" + r.status}>
            {r.status !== "plain" && <StatBadge status={r.status} />}
            {/* Interface leaves stay RAW (lowercase verbs, never humanized) —
                only the anonymous sentinel is prettied. */}
            <span className="dd-key">{interfaceDisplayName({ name: r.name })}</span>
            {r.typeChange && (
              <span className="dd-val">
                <span className="dd-old">{r.typeChange.from}</span>
                <span className="dd-val-arrow">→</span>
                <span className="dd-new">{r.typeChange.to}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── process / subprocess details ──────────────────────────────────────────────

/** Render a step key (`<idOrName>[.<rest>]`) readably: map the head id to its
 *  module name, strip the `name:` sentinel, keep the interface path. */
function prettyStep(key: string, idToName: ReadonlyMap<string, string>): string {
  const dot = key.indexOf(".");
  const head = dot === -1 ? key : key.slice(0, dot);
  const rest = dot === -1 ? "" : key.slice(dot + 1);
  const name = head.startsWith("name:") ? head.slice(5) : idToName.get(head) ?? head;
  return rest ? `${name}.${rest}` : name;
}

function ProcessDetails({
  diff,
  kind,
  idToName,
}: {
  diff: ProcessDiff | SubprocessDiff;
  kind: "process" | "subprocess";
  idToName: ReadonlyMap<string, string>;
}) {
  const status = diff.status;
  const isWhole = status === "added" || status === "removed";
  const name =
    diff.ownerPath && diff.ownerPath.length > 0 ? `${diff.ownerPath.join(".")}.${diff.name}` : diff.name;
  const addedSteps = diff.steps.added;
  const removedSteps = diff.steps.removed;
  const reordered = diff.steps.reordered;

  return (
    <aside className="dd-panel">
      <header className="dd-header">
        <div className="dd-eyebrow">
          <span className="dd-kind">{kind}</span>
          <StatusPill status={status} />
        </div>
        <h2 className="dd-name">{displayPath(name)}</h2>

        {diff.nameChange && <RenameLine from={diff.nameChange.from} to={diff.nameChange.to} />}
        {diff.ownerChange && (
          <div className="dd-rename" title="Moved to a new owner">
            <span className="dd-rename-from">{diff.ownerChange.from.length ? displayPath(diff.ownerChange.from.join(".")) : "(root)"}</span>
            <span className="dd-rename-arrow">→</span>
            <span className="dd-rename-to">{diff.ownerChange.to.length ? displayPath(diff.ownerChange.to.join(".")) : "(root)"}</span>
          </div>
        )}

        {isWhole ? (
          <div className="dd-summary dd-summary--quiet">
            {status === "added" ? `New ${kind}` : `Removed ${kind}`}
          </div>
        ) : (
          <div className="dd-summary">
            {addedSteps.length > 0 && (
              <span className="dd-chip dd-chip--added">
                <span className="dd-chip-sigil">+</span>
                {addedSteps.length} step{addedSteps.length === 1 ? "" : "s"}
              </span>
            )}
            {removedSteps.length > 0 && (
              <span className="dd-chip dd-chip--removed">
                <span className="dd-chip-sigil">−</span>
                {removedSteps.length} step{removedSteps.length === 1 ? "" : "s"}
              </span>
            )}
            {reordered && (
              <span className="dd-chip dd-chip--modified">
                <span className="dd-chip-sigil">↻</span>
                reordered
              </span>
            )}
            {addedSteps.length === 0 && removedSteps.length === 0 && !reordered && (
              <span className="dd-summary dd-summary--quiet">No step changes</span>
            )}
          </div>
        )}
      </header>

      <div className="dd-body">
        {isWhole ? (
          <StepSection
            title="Steps"
            steps={status === "removed" ? removedSteps : addedSteps}
            status="plain"
            idToName={idToName}
          />
        ) : (
          <>
            <StepSection title="Added steps" steps={addedSteps} status="added" idToName={idToName} />
            <StepSection title="Removed steps" steps={removedSteps} status="removed" idToName={idToName} />
          </>
        )}
      </div>
    </aside>
  );
}

function StepSection({
  title,
  steps,
  status,
  idToName,
}: {
  title: string;
  steps: readonly StepKey[];
  status: RowStatus;
  idToName: ReadonlyMap<string, string>;
}) {
  if (steps.length === 0) return null;
  return (
    <section className="dd-section">
      <SectionHead title={title} count={status === "plain" ? 0 : steps.length} />
      <ul className="dd-list">
        {steps.map((s, i) => (
          <li key={`${s.from}>${s.to}-${i}`} className={"dd-row dd-row--" + status}>
            {status !== "plain" && <StatBadge status={status} />}
            <span className="dd-key dd-step">
              <span className="dd-step-from">{prettyStep(s.from, idToName)}</span>
              <span className="dd-step-arrow">→</span>
              <span className="dd-step-to">{prettyStep(s.to, idToName)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── view details ──────────────────────────────────────────────────────────────

function ViewDetails({ id, status }: { id: string; status: Status }) {
  return (
    <aside className="dd-panel">
      <header className="dd-header">
        <div className="dd-eyebrow">
          <span className="dd-kind">view</span>
          <StatusPill status={status} />
        </div>
        <h2 className="dd-name">{id}</h2>
        <div className="dd-summary dd-summary--quiet">
          {status === "added"
            ? "New view"
            : status === "removed"
              ? "Removed view"
              : "View changed"}
        </div>
      </header>
    </aside>
  );
}

// ── shared bits ───────────────────────────────────────────────────────────────

function RenameLine({ from, to }: { from: string; to: string }) {
  return (
    <div className="dd-rename" title="Renamed">
      <span className="dd-rename-from">{displayLabel(from)}</span>
      <span className="dd-rename-arrow">→</span>
      <span className="dd-rename-to">{displayLabel(to)}</span>
    </div>
  );
}

function ChangeChips({
  added,
  modified,
  removed,
  emptyLabel,
}: {
  added: number;
  modified: number;
  removed: number;
  emptyLabel: string;
}) {
  if (added + modified + removed === 0) {
    return <div className="dd-summary dd-summary--quiet">{emptyLabel}</div>;
  }
  return (
    <div className="dd-summary">
      {added > 0 && (
        <span className="dd-chip dd-chip--added">
          <span className="dd-chip-sigil">+</span>
          {added} added
        </span>
      )}
      {modified > 0 && (
        <span className="dd-chip dd-chip--modified">
          <span className="dd-chip-sigil">~</span>
          {modified} changed
        </span>
      )}
      {removed > 0 && (
        <span className="dd-chip dd-chip--removed">
          <span className="dd-chip-sigil">−</span>
          {removed} removed
        </span>
      )}
    </div>
  );
}

function SectionHead({ title, count }: { title: string; count: number }) {
  return (
    <h3 className="dd-section-head">
      <span className="dd-section-title">{title}</span>
      {count > 0 && <span className="dd-section-count">{count}</span>}
    </h3>
  );
}
